import type { ComponentsDefinition } from '@eneco/cms';
import { cmsComponents } from '@eneco/cms-components';
import dynamic from 'next/dynamic';

/**
 * The CMS-agnostic registry for this container: the shared one plus what only this
 * container mounts.
 *
 * Applications are registered per container on the Sitecore lane too, in
 * `sitecoreComponents.ts`, and for the same reason: they cannot live in the shared
 * registry. `lib:cms-components` may not depend on `app:next` (see `depConstraints`
 * in `eslint.config.mjs`), and every container passes that one map, so a flow put
 * there would also be pulled into the containers that never render it.
 *
 * The cookie wall is one of those applications. It sits in the page's `jss-meta`
 * placeholder, which `CmsLayout` renders on this lane too, and reads its content
 * through `@eneco/cms`, so the same component serves both lanes. The chat sits there too; its CMS-lane entry
 * is `ChatInner`, which reads its content through `@eneco/cms`.
 *
 * `AffiliateCalculationTool` is registered for the same reason, and reads its content
 * through `@eneco/cms` too.
 *
 * `AdvancedCalculationTool` is registered for the same reason, and reads its content
 * through `@eneco/cms` too.
 *
 * `Unsubscribe` is registered for the same reason, and reads its content
 * through `@eneco/cms` too.
 *
 * So is the heat comparison tool, registered as its inner component: this lane's
 * `Placeholder` already hands it its rendering, which the Sitecore lane's bridge builds itself.
 * The inspiration advice filter is registered the same way.
 *
 * `AppPlaceholder` looks the key up by the name after `jss-app-`, so it has to match
 * the placeholder on the page, and it matches the Sitecore rendering name one to one.
 *
 * Without this file the two lanes are not comparable on this container: the flow is
 * registered on the Sitecore lane here (`sitecoreComponents.ts`), so a route renders
 * by default and renders empty under `?_cms=sanity`, which reads as a content problem
 * when it is only a missing registration.
 *
 * A flow is added here once its steps read their content through `@eneco/cms`. Until
 * then it stays out: its steps still read Sitecore directly, so on this lane it would
 * fail at the first step rather than render.
 */
const components: ComponentsDefinition = {
  ...cmsComponents,
  AdvancedCalculationTool: dynamic(() => import('@apps/flows-nl-eneco-energyFlow/AdvancedCalculationTool')),
  AffiliateCalculationTool: dynamic(() => import('@apps/flows-nl-eneco-energyFlow/CalculationTool')),
  Chat: dynamic(() => import('@apps/chat-shared-multilabel/Chat').then(mod => mod.ChatInner)),
  CookieWall: dynamic(() => import('@apps/cookiewall-nl-multilabel/CookieWall')),
  FlowThankYouHero: dynamic(() => import('@apps/flowthankyouhero-nl-eneco/FlowThankYouHeroInner')),
  HeatComparisonTool: dynamic(() =>
    import('@apps/calculator-nl-eneco/HeatComparisonCalculator').then(mod => mod.HeatComparisonCalculatorInner),
  ),
  InspirationAdviceFilter: dynamic(() =>
    import('@apps/inspirationadvicefilter-nl-eneco/InspirationAdviceFilter').then(
      mod => mod.InspirationAdviceFilterInner,
    ),
  ),
  'legislation-compass-flow': dynamic(
    () => import('@apps/flows-nl-eneco-legislationCompassFlow/LegislationCompassFlow'),
  ),
  Unsubscribe: dynamic(() => import('@apps/unsubscribe-shared-multilabel/Unsubscribe')),
};

export default components;
